import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Upload, Download, Trash2, X, File, Image, FileSpreadsheet } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".txt", ".xls", ".xlsx"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const getFileIcon = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  if (['jpg', 'jpeg', 'png'].includes(ext)) return <Image className="w-5 h-5 text-blue-500" />;
  if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  if (['pdf'].includes(ext)) return <FileText className="w-5 h-5 text-red-500" />;
  return <File className="w-5 h-5 text-slate-500" />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// XHR-based file upload to avoid fetch issues
const uploadFileXHR = (url, file, token) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);
    
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = 60000; // 60 seconds for file upload
    
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      } catch (e) {
        resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    
    xhr.send(formData);
  });
};

// XHR-based API request
const apiRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    xhr.timeout = 15000;
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      } catch (e) {
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data: {} });
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timed out'));
    xhr.send(options.body || null);
  });
};

const DocumentManager = ({ bookingId, isOpen, onClose, bookingDate }) => {
  const { token, user } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && bookingId) {
      fetchDocuments();
    }
  }, [isOpen, bookingId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiRequest(`${API}/bookings/${bookingId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (ok) {
        setDocuments(data);
      }
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file extension
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }

    // Check document count
    if (documents.length >= 5) {
      toast.error("Maximum 5 documents allowed per booking");
      return;
    }

    setUploading(true);
    try {
      const { ok, data } = await uploadFileXHR(
        `${API}/bookings/${bookingId}/documents`,
        file,
        token
      );

      if (ok) {
        toast.success("Document uploaded successfully");
        fetchDocuments();
      } else {
        toast.error(data.detail || "Failed to upload document");
      }
    } catch (error) {
      toast.error(error.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleDownload = async (document) => {
    try {
      // Create a download link using XHR
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API}/documents/${document.document_id}/download`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.responseType = 'blob';
      
      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const url = window.URL.createObjectURL(blob);
          const a = window.document.createElement('a');
          a.href = url;
          a.download = document.original_filename;
          window.document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        } else {
          toast.error("Failed to download document");
        }
      };
      
      xhr.onerror = () => toast.error("Download failed");
      xhr.send();
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const { ok, data } = await apiRequest(`${API}/documents/${documentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (ok) {
        toast.success("Document deleted");
        fetchDocuments();
      } else {
        toast.error(data.detail || "Failed to delete document");
      }
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Documents
          </DialogTitle>
          <DialogDescription>
            Booking on {bookingDate} • {documents.length}/5 files
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Section */}
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors">
            <input
              type="file"
              id="document-upload"
              className="hidden"
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xls,.xlsx"
              disabled={uploading || documents.length >= 5}
            />
            <label 
              htmlFor="document-upload" 
              className={`cursor-pointer ${uploading || documents.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              {uploading ? (
                <p className="text-sm text-slate-600">Uploading...</p>
              ) : documents.length >= 5 ? (
                <p className="text-sm text-slate-600">Maximum files reached</p>
              ) : (
                <>
                  <p className="text-sm font-medium text-slate-700">Click to upload</p>
                  <p className="text-xs text-slate-500 mt-1">
                    PDF, DOC, DOCX, JPG, PNG, TXT, XLS (max 10MB)
                  </p>
                </>
              )}
            </label>
          </div>

          {/* Documents List */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
              </div>
            ) : documents.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No documents uploaded yet</p>
            ) : (
              documents.map((doc) => (
                <div
                  key={doc.document_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getFileIcon(doc.original_filename)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {doc.original_filename}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatFileSize(doc.file_size)} • Uploaded by {doc.uploader_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      data-testid={`download-doc-${doc.document_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(doc)}
                      className="h-8 w-8 p-0"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {doc.uploader_id === user?.user_id && (
                      <Button
                        data-testid={`delete-doc-${doc.document_id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(doc.document_id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentManager;
